import type { Company, Image } from "@prisma/client";
import isEmpty from "~/kernel/arrays/isEmpty";
import { Link } from "@remix-run/react";
import ROUTES from "~/kernel/routing/ROUTES";
import BANNER_DIMENSIONS from "~/constants/company/BANNER_DIMENSIONS";
import fullAddress from "~/kernel/adress/fullAddress";
import useTranslation from "~/kernel/internationalization/useTranslation";
import useLoaderData from "./useLoaderData";
import useAnalytics from "~/analytics/components/useAnalytics";

export default function PopularCompanies() {
  const { popularCompanies } = useLoaderData();
  const { tt } = useTranslation("index");

  if (isEmpty(popularCompanies)) {
    return <div>{tt("noSalonsYet")}</div>;
  }

  return (
    <div className="grid gap-5 gap-y-10 md:grid-cols-3">
      {popularCompanies.map((company) => (
        <CompanyGridItem key={company.id} company={company} />
      ))}
    </div>
  );
}

type TCompanyListItem = Omit<
  Company,
  "createdAt" | "updatedAt" | "deletedAt"
> & {
  bannerUrl: string;
  banner: Pick<Image, "width" | "height"> | null;
};

type CompanyGridItemProps = {
  company: TCompanyListItem;
};

function CompanyGridItem({ company }: CompanyGridItemProps) {
  const analytics = useAnalytics();

  return (
    <Link
      className="cursor-pointer overflow-hidden hover:underline"
      to={ROUTES.company.path({ params: { slug: company.slug } })}
      onClick={() =>
        analytics.track({
          type: "OPEN_COMPANY_FROM_START_PAGE_V1",
          payload: { companyId: company.id, companySlug: company.slug },
        })
      }
    >
      <div className="flex flex-col">
        <img
          className="rounded-md border border-slate-200"
          src={company.bannerUrl}
          alt={`Banner for ${company.name}`}
          width={BANNER_DIMENSIONS.width}
          height={BANNER_DIMENSIONS.height}
        />
        <div className="py-5">
          <div className="text-slate-800 text-xl font-bold">{company.name}</div>
          <p className="text-sm">{fullAddress(company)}</p>
        </div>
      </div>
    </Link>
  );
}
